import { Injectable } from '@angular/core';
import { StorageService, StorageTypes } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { PaymentCartTypeType } from '../models/payment-type';
import { AwbLookUpService } from './awb-look-up.service';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';

@Injectable({
    providedIn: 'root',
})
export class PaymentCartTypeHandlerService {
    private _paymentCartType: BehaviorSubject<PaymentCartTypeType> = new BehaviorSubject<PaymentCartTypeType>(
        PaymentCartTypeType.NONE
    );

    constructor(
        private cartBillService: CartBillService,
        private awbLookUpService: AwbLookUpService,
        private storageService: StorageService,
        private configService: ConfigService
    ) {
        const paymentCartType: PaymentCartTypeType = this.storageService.getElement(
            'paymentCartType',
            StorageTypes.SESSION
        );
        if (paymentCartType !== null) {
            this.setPaymentCartType(paymentCartType);
        }
    }

    get instant_payment_cart_type(): PaymentCartTypeType {
        return this._paymentCartType.value;
    }

    getPaymentCartType(): Observable<PaymentCartTypeType> {
        return this._paymentCartType.asObservable().pipe(distinctUntilChanged());
    }

    setPaymentCartType(paymentType: PaymentCartTypeType): void {
        this._paymentCartType.next(paymentType);
        this.storageService.saveElement('paymentCartType', paymentType, StorageTypes.SESSION);
    }

    getInitialTab(): Observable<PaymentCartTypeType> {
        return this.getPaymentCartTypeByChargesInCart().pipe(
            switchMap((paymentCartType) => {
                const isEnableTabsCharges = this.configService.isEnableTabsCharges();

                if (isEnableTabsCharges && paymentCartType === PaymentCartTypeType.NONE) {
                    return this.getPaymentCartByUserSelection().pipe(
                        switchMap((userSelection) => {
                            if (userSelection !== PaymentCartTypeType.NONE) {
                                return of(userSelection);
                            }

                            return this.getPaymentCartTypeBySearchResponse();
                        })
                    );
                }

                if (paymentCartType === PaymentCartTypeType.NONE) {
                    return this.getPaymentCartByUserSelection();
                }

                return of(paymentCartType);
            }),
            distinctUntilChanged<PaymentCartTypeType>()
        );
    }

    getPaymentCartTypeByChargesInCart(): Observable<PaymentCartTypeType> {
        return this.cartBillService.getCart$().pipe(
            map((cart) => {
                if (cart.length) {
                    return cart[0].hawb ? PaymentCartTypeType.HAWB : PaymentCartTypeType.MAWB;
                }

                return PaymentCartTypeType.NONE;
            })
        );
    }

    getPaymentCartTypeBySearchResponse(): Observable<PaymentCartTypeType> {
        return this.awbLookUpService.awbContainsHAWBs$().pipe(
            map((awbContainsHAWBs) => {
                if (awbContainsHAWBs) {
                    return this.configService.getPaymentCartType();
                }

                return PaymentCartTypeType.MAWB;
            })
        );
    }

    getPaymentCartByUserSelection(): Observable<PaymentCartTypeType> {
        return this.getPaymentCartType();
    }
}
